import React, { useState, useMemo } from "react";
import styled from "styled-components/macro";
import {
  useRollupsToDisplay,
  useVisibleProcesses,
  useTrendsPageFilterState,
} from "utils/customHooks/genericHooks";
import { MachineBreakdownTitleRow } from "./subcomponents/TitleRow";
import useContainerDimensions from "utils/useContainerDimensions";
import { BREAKPOINT_TABLET } from "constants/constants";
import { MachineBreakdownRowCollapsible } from "./subcomponents/MachineBreakdownRowCollapsible";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { Button } from "@mui/material";
import { blueButton } from "constants/colors";
import { METRICS_COLUMNS_SPACER_WIDTH } from "./machineBreakdownChartUtils";
import { useSelector } from "store/useSelector";
import { useShouldUseDataDumpsForMachineBreakdownChart } from "utils/customHooks/whichServerHooks";
import { useMapDataDumpToRollup } from "../TrendChart/TrendChartContent/trendChartContentUtils";
import { addShiftlyRollupsIntoSingleRollup } from "../TrendChart/trendChartUtils";
import { useFocusRange } from "utils/customHooks/genericHooks";
import { RollupWithTagsAndCountsAndRange } from "types/types";

/** For each process, display a row including processName - metrics - butterfly-like horizontal barstack
 *
 * Each process row can expand to reveal a PnlSummaryChart for that process
 */
const MachineBreakdownChart = () => {
  const isStorybook = useIsStorybook();
  const visibleProcesses = useVisibleProcesses();
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const rollupsDisplay = useRollupsToDisplay();
  const shouldUseDataDumps = useShouldUseDataDumpsForMachineBreakdownChart();
  const [containerRef, dimensions] = useContainerDimensions();
  /** for small widths, move the metrics to a separate toggleable "page" */
  const isTabletOrLarger = (dimensions.width || 0) > BREAKPOINT_TABLET;
  const showMetrics = isTabletOrLarger;

  // State to control global expand/collapse of all rows
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if a tag is selected in the filter state
  const { trendsPageFilterState } = useTrendsPageFilterState();
  const isTagSelected = trendsPageFilterState.tag !== "All";
  const selectedTag = isTagSelected ? trendsPageFilterState.tag : null;

  // Check if a shift is selected in the filter state
  const isShiftSelected = trendsPageFilterState.shiftName !== "All";
  const selectedShift = isShiftSelected
    ? trendsPageFilterState.shiftName
    : null;

  // Get focus range for shift filtering
  const focusRange = useFocusRange();

  // Get all rollups data for shift filtering
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );

  // only one row can be expanded at a time, unless a tag is selected
  const [expandedRow, setExpandedRow] = useState(isStorybook ? 1 : null);

  // Updated for new UI flow where metrics are always shown in expanded state
  // This is kept for backward compatibility with other parts of the code
  const showMetricsViewInsteadOfTagBreakdown = false;
  const mapDataDumpToRollup = useMapDataDumpToRollup();

  // Filter out processes that don't have data for the selected tag
  const visibleProcessesForCurrentFilter = useMemo(() => {
    if (!isTagSelected || !selectedTag) {
      return visibleProcesses;
    }

    return visibleProcesses.filter(process => {
      const rollup =
        shouldUseDataDumps && dataDumpsFullRangeByProcess[process.id]
          ? mapDataDumpToRollup(dataDumpsFullRangeByProcess[process.id])
          : rollupsDisplay[process.id];

      if (!rollup || !rollup.tags) {
        return false;
      }

      // Check if this process has any data for the selected tag
      for (const category in rollup.tags) {
        if (rollup.tags[category][selectedTag] > 0) {
          return true;
        }
      }

      return false;
    });
  }, [
    isTagSelected,
    selectedTag,
    visibleProcesses,
    rollupsDisplay,
    shouldUseDataDumps,
    dataDumpsFullRangeByProcess,
    mapDataDumpToRollup,
  ]);

  // Create the shift-filtered rollups for each process using the same approach as PnlSummaryChart
  const processRollupsFilteredByShift = useMemo(() => {
    if (!isShiftSelected || !selectedShift || !focusRange) {
      return {};
    }

    const filteredRollups = {};

    // For each process, apply shift filtering using addShiftlyRollupsIntoSingleRollup
    visibleProcesses.forEach(process => {
      const processId = process.id;
      const rollupsForProcess = allRollupsMetricTrendChartBarsArrays[processId];

      if (rollupsForProcess) {
        // Filter rollups for just this shift
        const rollupsForShift = rollupsForProcess.filter(
          ({ shift }) => shift?.name === selectedShift
        );

        // Use the same function that PnlSummaryChart uses to combine shift-specific rollups
        const shiftFilteredRollup = addShiftlyRollupsIntoSingleRollup({
          rollupsArr: rollupsForShift,
          focusRange,
          shiftName: selectedShift,
        });

        // Store the filtered rollup for this process
        filteredRollups[processId] = shiftFilteredRollup;
      }
    });

    return filteredRollups;
  }, [
    isShiftSelected,
    selectedShift,
    focusRange,
    visibleProcesses,
    allRollupsMetricTrendChartBarsArrays,
  ]);

  // Function to get the properly filtered rollup for a process
  const getProcessRollup = (
    processId: string
  ): RollupWithTagsAndCountsAndRange => {
    // If shift filtered and we have a filtered rollup for this process, use it
    if (
      isShiftSelected &&
      selectedShift &&
      processRollupsFilteredByShift[processId]
    ) {
      return processRollupsFilteredByShift[processId];
    }

    // Otherwise, use the normal rollup
    return shouldUseDataDumps && dataDumpsFullRangeByProcess[processId]
      ? mapDataDumpToRollup(dataDumpsFullRangeByProcess[processId])
      : rollupsDisplay[processId];
  };

  return (
    <MachineBreakdownChartStyles
      data-component-name={"Machine Breakdown Chart"}
      ref={containerRef}
    >
      <BtnExpandCollapseMetrics
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        className={isTabletOrLarger ? undefined : "mobile-view"}
      />
      <MachineBreakdownTitleRow
        {...{ showMetrics, showMetricsViewInsteadOfTagBreakdown }}
      />
      {(isTagSelected
        ? visibleProcessesForCurrentFilter
        : visibleProcesses
      ).map((process, idx) => {
        // Get the properly filtered rollup data for this process
        const filteredRollup = getProcessRollup(process.id);

        return (
          <React.Fragment key={process.id}>
            <MachineBreakdownRowCollapsible
              {...{
                showMetrics,
                process,
                processIdx: idx,
                rollupDisplay: filteredRollup,
                open: isExpanded || isTagSelected || expandedRow === idx,
                setExpandedRow,
                showMetricsViewInsteadOfTagBreakdown,
                isTagSelected,
                selectedTag,
                isShiftSelected,
                selectedShift,
              }}
            />
          </React.Fragment>
        );
      })}
    </MachineBreakdownChartStyles>
  );
};

export default MachineBreakdownChart;

const MachineBreakdownChartStyles = styled.div`
  font-size: 15px;
  max-width: 1200px;
  margin: auto;
  .spacer {
    width: ${METRICS_COLUMNS_SPACER_WIDTH}px;
    height: ${METRICS_COLUMNS_SPACER_WIDTH}px;
  }
  padding-top: 12px;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    padding-top: 0;
  }
`;

/** Button to expand/collapse all rows in the chart
 * Shows metrics and Top 3 Losses charts for all processes when expanded
 */
function BtnExpandCollapseMetrics({
  isExpanded,
  setIsExpanded,
  className,
}: {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
}) {
  return (
    <StyledBtnExpandCollapseMetrics
      onClick={() => setIsExpanded(prev => !prev)}
      className={className}
    >
      {isExpanded ? "Collapse" : "Expand"}
    </StyledBtnExpandCollapseMetrics>
  );
}

const StyledBtnExpandCollapseMetrics = styled(Button)`
  position: absolute !important;
  color: ${blueButton} !important;
  text-transform: none !important;
  font-size: 17px !important;
  font-family: "Open Sans" !important;
  top: 0px;
  right: 8px;
  z-index: 1;

  &.mobile-view {
    /* Additional styles for mobile view if needed */
  }
`;
